import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Facebook,
  Globe,
  Instagram,
  MapPin,
  Phone,
  ShoppingBag,
  ImagePlus,
  Twitter,
} from "lucide-react";
import Images from "../../Images";
import StoreCatalogPreview from "./StoreCatalogPreview";
import { arePromoCardsComplete, isPromoCardsTouched, normalizePromoCards } from "../../utils/storeDefaults";
import { isYouTubeHighlightVisible } from "../../utils/youtubeEmbed";

const StorePreview = ({ storeConfig, vendor, deals = [], previewMode = "desktop" }) => {
  const [bannerIndex, setBannerIndex] = useState(0);
  const isMobile = previewMode === "mobile";
  const isTablet = previewMode === "tablet";

  const bannerHeight = isMobile ? "h-36" : isTablet ? "h-44" : "h-48 sm:h-52";
  const bannerTextBottom = isMobile ? "bottom-12" : isTablet ? "bottom-14" : "bottom-16 sm:bottom-[4.5rem]";
  const bannerDotsBottom = isMobile ? "bottom-[2.75rem]" : isTablet ? "bottom-[3.25rem]" : "bottom-[3.75rem] sm:bottom-16";

  const activeBanners = useMemo(
    () =>
      (storeConfig?.banners || [])
        .filter((banner) => banner.image)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [storeConfig?.banners]
  );

  const activeDeals = useMemo(
    () =>
      (deals || []).filter(
        (deal) =>
          deal.status?.toLowerCase() === "active" ||
          deal.status?.toLowerCase() === "approved"
      ),
    [deals]
  );

  const brandName = vendor?.brand_name || "Your Brand";
  const brandLogo = vendor?.logo || vendor?.company_logo || null;
  const category = vendor?.brand_category || "General";
  const location = vendor?.location || "Ghana";

  useEffect(() => {
    if (bannerIndex >= activeBanners.length) {
      setBannerIndex(0);
    }
  }, [activeBanners.length, bannerIndex]);

  useEffect(() => {
    if (activeBanners.length <= 1) return undefined;

    const timer = setInterval(() => {
      setBannerIndex((prev) => (prev + 1) % activeBanners.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [activeBanners.length]);

  const currentBanner = activeBanners[bannerIndex];

  const promoCards = useMemo(
    () => normalizePromoCards(storeConfig?.promoCards),
    [storeConfig?.promoCards]
  );
  const showPromoCards = isPromoCardsTouched(promoCards);
  const promoCardsComplete = arePromoCardsComplete(promoCards);
  const showHighlightVideo = isYouTubeHighlightVisible(storeConfig?.highlightVideo);

  return (
    <div className="min-h-full bg-[#FAFAFA]">
      {/* Consumer navbar strip */}
      <div className="sticky top-0 z-30 px-3 py-2 bg-white border-b border-gray-100">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <img src={Images.logo} alt="Dealboxx" className="h-5" />
          <div className="flex items-center gap-2 text-gray-400">
            <ShoppingBag className="w-3.5 h-3.5" />
            <div className="w-6 h-6 rounded-full bg-gray-100" />
          </div>
        </div>
      </div>

      {/* Hero banners */}
      <section className="relative overflow-hidden bg-gray-900">
        {activeBanners.length > 0 ? (
          <>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentBanner?.id || bannerIndex}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45 }}
                className={`relative ${bannerHeight}`}
              >
                <img
                  src={currentBanner.image}
                  alt={currentBanner.title || "Store banner"}
                  className="object-cover w-full h-full"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className={`absolute left-0 right-0 px-3 sm:px-4 ${bannerTextBottom}`}>
                  {currentBanner.title ? (
                    <h2 className="text-sm sm:text-base font-bold text-white drop-shadow-lg line-clamp-1">
                      {currentBanner.title}
                    </h2>
                  ) : null}
                  {currentBanner.subtitle ? (
                    <p className="mt-0.5 text-xs text-white/90 drop-shadow-md line-clamp-1">
                      {currentBanner.subtitle}
                    </p>
                  ) : null}
                </div>
              </motion.div>
            </AnimatePresence>

            {activeBanners.length > 1 ? (
              <>
                <button
                  type="button"
                  onClick={() =>
                    setBannerIndex(
                      (prev) => (prev - 1 + activeBanners.length) % activeBanners.length
                    )
                  }
                  className="absolute left-3 top-1/2 z-10 p-2 text-white -translate-y-1/2 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/45"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setBannerIndex((prev) => (prev + 1) % activeBanners.length)
                  }
                  className="absolute right-3 top-1/2 z-10 p-2 text-white -translate-y-1/2 rounded-full bg-black/30 backdrop-blur-sm hover:bg-black/45"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <div className={`absolute left-1/2 z-10 flex gap-1.5 -translate-x-1/2 ${bannerDotsBottom}`}>
                  {activeBanners.map((banner, index) => (
                    <button
                      key={banner.id}
                      type="button"
                      onClick={() => setBannerIndex(index)}
                      className={`h-1.5 rounded-full transition-all ${
                        index === bannerIndex ? "w-6 bg-white" : "w-1.5 bg-white/50"
                      }`}
                    />
                  ))}
                </div>
              </>
            ) : null}
          </>
        ) : (
          <div
            className={`flex flex-col items-center justify-center text-center px-6 ${bannerHeight} bg-gradient-to-br from-[#FF6C6C] via-[#FF8585] to-[#FFB4B4]`}
          >
            <ImagePlus className="w-5 h-5 mb-2 text-white/90" />
            <p className="text-xs font-medium text-white/90">
              Upload banners to showcase your store
            </p>
          </div>
        )}
      </section>

      {/* Store identity — slight overlap with hero banner */}
      <section className="relative px-3 pb-4 -mt-3 z-10">
        <div className="max-w-6xl mx-auto">
          <div className="p-3 sm:p-4 bg-white border border-gray-100 rounded-2xl shadow-md">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex items-center justify-center w-11 h-11 overflow-hidden bg-gray-50 border border-white rounded-xl shadow-sm shrink-0">
                  {brandLogo ? (
                    <img src={brandLogo} alt={brandName} className="object-contain w-8 h-8" />
                  ) : (
                    <span className="text-lg font-bold text-[#FF6C6C]">
                      {brandName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <h1 className="text-base font-bold text-gray-900 truncate">{brandName}</h1>
                  <p className="text-xs text-gray-500 truncate">{category}</p>
                  <div className="flex items-center gap-1 mt-0.5 text-[11px] text-gray-400">
                    <MapPin className="w-3 h-3 shrink-0" />
                    <span className="truncate">{location}</span>
                  </div>
                </div>
              </div>

              {storeConfig?.showContactBar !== false ? (
                <div className="flex flex-wrap gap-1.5 sm:ml-auto">
                  {vendor?.website ? (
                    <a
                      href={vendor.website}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100"
                    >
                      <Globe className="w-3 h-3" />
                      Website
                    </a>
                  ) : null}
                  {vendor?.phone_number ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-gray-600 bg-gray-50 rounded-lg">
                      <Phone className="w-3 h-3" />
                      {vendor.phone_number}
                    </span>
                  ) : null}
                  {vendor?.facebook ? (
                    <span className="inline-flex items-center justify-center w-7 h-7 text-blue-600 bg-blue-50 rounded-lg">
                      <Facebook className="w-3.5 h-3.5" />
                    </span>
                  ) : null}
                  {vendor?.instagram ? (
                    <span className="inline-flex items-center justify-center w-7 h-7 text-pink-600 bg-pink-50 rounded-lg">
                      <Instagram className="w-3.5 h-3.5" />
                    </span>
                  ) : null}
                  {vendor?.twitter ? (
                    <span className="inline-flex items-center justify-center w-7 h-7 text-sky-600 bg-sky-50 rounded-lg">
                      <Twitter className="w-3.5 h-3.5" />
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            {storeConfig?.tagline ? (
              <p className="mt-2 text-xs font-medium text-[#FF6C6C]">{storeConfig.tagline}</p>
            ) : null}

            {storeConfig?.about ? (
              <p className="mt-1.5 text-xs leading-relaxed text-gray-600 line-clamp-2">
                {storeConfig.about}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      {/* Category highlights — below banner & brand section */}
      {showPromoCards ? (
        <section className="px-3 pt-1 pb-3">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {promoCards.map((card, index) => (
                <div
                  key={card.id}
                  className={`overflow-hidden rounded-xl border bg-[#F5F5F5] ${
                    card.image ? "border-gray-200" : "border-dashed border-amber-200"
                  }`}
                >
                  {card.title ? (
                    <p className="px-1.5 pt-2 pb-1 text-[10px] font-semibold text-center text-gray-800 truncate sm:text-xs sm:px-2">
                      {card.title}
                    </p>
                  ) : null}
                  <div className="relative aspect-[4/3] mx-1.5 mb-1.5 sm:mx-2 sm:mb-2 rounded-lg overflow-hidden bg-white">
                    {card.image ? (
                      <img
                        src={card.image}
                        alt={card.title || `Highlight ${index + 1}`}
                        className="object-contain w-full h-full p-1"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center w-full h-full text-center bg-amber-50/50">
                        <p className="text-[9px] font-medium text-amber-600 px-1 sm:text-[10px]">
                          Image {index + 1} required
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {!promoCardsComplete ? (
              <p className="mt-2 text-[10px] text-center text-amber-600">
                Upload all 3 images to include this section on your live store
              </p>
            ) : null}
          </div>
        </section>
      ) : null}

      {showHighlightVideo ? (
        <section className="px-3 pb-3">
          <div className="max-w-6xl mx-auto overflow-hidden border border-gray-200 rounded-2xl shadow-md">
            {storeConfig.highlightVideo?.title ? (
              <p className="px-3 py-2 text-xs font-bold text-gray-800 border-b border-gray-100 bg-white">
                {storeConfig.highlightVideo.title}
              </p>
            ) : null}
            <div className="relative w-full aspect-[1697/602] bg-black">
              <iframe
                title={storeConfig.highlightVideo?.title || "Store spotlight video"}
                src={storeConfig.highlightVideo.embedUrl}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>
        </section>
      ) : null}

      {/* Product catalog — marketplace layout */}
      <StoreCatalogPreview
        brandName={brandName}
        deals={activeDeals}
        previewMode={previewMode}
        isPublished={storeConfig?.isPublished}
      />
    </div>
  );
};

export default StorePreview;
